import {connect} from 'react-redux';

import {computeCrmSyncSourceDisplayName} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {extractMicrosoftDynamicsEnvironmentUrl} from '../../../../../../../../../src/helpers/integrationHelpers';
import {ResolveDispatchProps} from '../../../../../../../../../src/helpers/reduxHelpers';
import {
  CrmSyncSourcesEnum,
  mapCrmSyncSourceToIntegrationType,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {AppState} from '../../../../../../../../../src/store/appState';
import {
  getIsCrmSyncLoading,
  makeGetAccountCrmSyncViaSource,
  makeGetCardCrmSyncViaSource,
} from '../../../../../../../../../src/store/data/crmSync/crmSyncsSelectors';
import {
  createIntegrationAsyncAction,
  fetchAllIntegrationsAsyncAction,
  signInToIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
  updateIntegrationAsyncAction,
} from '../../../../../../../../../src/store/data/integrations/integrationsActions';
import {getCrmIntegration} from '../../../../../../../../../src/store/data/integrations/integrationSelectors';
import {fetchIntegrationTypesAsyncAction} from '../../../../../../../../../src/store/data/integrationTypes/integrationTypesActions';
import {
  getIntegrationTypes,
  getIsIntegrationTypesLoading,
} from '../../../../../../../../../src/store/data/integrationTypes/integrationTypesSelectors';
import {getIsUserGlobalAdmin} from '../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../../srcCommon/helpers/reselectHelpers';
import {CrmSyncsLogin} from './crmSyncsLogin';

interface CrmSyncLoginContainerPublicProps {
  isConnected: boolean;
  isDisabled: boolean;
  hasExpiredCredentials: boolean;
  source: CrmSyncSourcesEnum;
  username?: string;
}

type CrmSyncLoginContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type CrmSyncLoginContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
export type CrmSyncLoginContainerProps = CrmSyncLoginContainerPublicProps &
  CrmSyncLoginContainerStateProps &
  CrmSyncLoginContainerDispatchProps;

/*
 * Selectors.
 */

const getMicrosoftDynamicsEnvironmentUrl = createSelector(
  [getCrmIntegration],
  extractMicrosoftDynamicsEnvironmentUrl,
);

function getIntegrationTypeId(_state: AppState, props: CrmSyncLoginContainerPublicProps) {
  return mapCrmSyncSourceToIntegrationType(props.source);
}

const getIntegrationType = createSelector(
  [getIntegrationTypeId, getIntegrationTypes],
  (integrationTypeId, integrationTypes) => {
    if (!integrationTypeId) {
      return undefined;
    }
    return integrationTypes[integrationTypeId];
  },
);

function makeGetHasAccountCrmSync() {
  return createSelector([makeGetAccountCrmSyncViaSource()], (accountCrmSync) => Boolean(accountCrmSync));
}

function makeGetHasCardCrmSync() {
  return createSelector([makeGetCardCrmSyncViaSource()], (cardCrmSync) => Boolean(cardCrmSync));
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CrmSyncLoginContainerPublicProps>()(() => ({
  isAdmin: getIsUserGlobalAdmin,
  existingMicrosoftDynamicsEnvironmentUrl: getMicrosoftDynamicsEnvironmentUrl,
  integration: getCrmIntegration,
  integrationType: getIntegrationType,
  isIntegrationTypesLoading: getIsIntegrationTypesLoading,
  isCrmSyncLoading: getIsCrmSyncLoading,
  hasAccountCrmSync: makeGetHasAccountCrmSync(),
  hasCardCrmSync: makeGetHasCardCrmSync(),
}));

const mapDispatchToProps = makeMapDispatchToProps<CrmSyncLoginContainerPublicProps>()(({source}) => {
  const integrationType = mapCrmSyncSourceToIntegrationType(source);

  return {
    createIntegrationAsync: createIntegrationAsyncAction,
    updateIntegrationAsync: updateIntegrationAsyncAction,
    fetchIntegrationTypesAsync: fetchIntegrationTypesAsyncAction,
    fetchAllIntegrationsAsync: fetchAllIntegrationsAsyncAction,
    signOutOfIntegrationAsync: () => signOutOfIntegrationAsyncAction(integrationType),
    signInToIntegrationAccount: (shouldUseTestEnv: boolean, stateData?: string) => {
      const crmSyncSourceName = computeCrmSyncSourceDisplayName(source);
      // For salesforce we need to press shift to login to the test env. For other crms we can test with our personal accounts.
      const hasTestEnvOption = source === CrmSyncSourcesEnum.SALESFORCE ? shouldUseTestEnv : false;
      if (integrationType) {
        return signInToIntegrationAccountAction(
          integrationType,
          crmSyncSourceName,
          hasTestEnvOption,
          stateData,
        );
      }
      return undefined;
    },
  };
});

export const CrmSyncLoginContainer = connect(mapStateToProps, mapDispatchToProps)(CrmSyncsLogin);
